import gsap from 'gsap'
import './Project.css'
import { splitReveal, splitConceal, SplitFx } from '../../utils/text.js'
import { DURATION, EASE, STAGGER } from '../../../shared/motion.js'

const MAX_SLOTS = 10

class AppProject extends HTMLElement {
    constructor() {
        super()

        this.projects = []
        this.allRows = [] // 預建的 MAX 列 DOM 封裝，不隨資料增減
        this.rows = [] // 目前使用中的列（allRows 的前 count 個）
        this.slotProjects = [] // 每個 slot 目前對應顯示的 project（hover 反查用）

        this.count = 0
        this.centerSlot = 0
        this.activeSlot = -1 // 目前 hover 中的 slot

        // 左下角 Name：每次換手都用全新的 slot 元素，彼此互不干擾，可無限快速連續觸發
        this._activeNameFx = null // 目前顯示中的 SplitFx
        this._nameFxPool = new Set() // 所有還在跑動畫、尚未自行移除的 SplitFx（供立即中斷用）

        // 整排 Project Num 的 Split 實例與動畫快取
        this._split = null
        this._splitAnimation = null
    }

    connectedCallback() {
        this._renderDOM()
        this._setupDOMReferences()
        this._initThemeColors()
    }

    // ─────────────────────────────────────────────────────────────
    // DOM & Initialization Helpers
    // ─────────────────────────────────────────────────────────────

    _renderDOM() {
        const rowsHTML = Array.from({ length: MAX_SLOTS }, () => /* html */ {
            return `
            <div class="item" style="display:none">
                <span class="item__num"></span>
            </div>
        `
        }).join('')

        this.innerHTML = /* html */ `
            <div class="panel">${rowsHTML}</div>
            <div class="name"></div>
        `
    }

    _setupDOMReferences() {
        this.panel = this.querySelector('.panel')
        this.name = this.querySelector('.name')

        // 預建 MAX_SLOTS 個列結構
        this.allRows = [...this.panel.querySelectorAll('.item')].map((el) => {
            return {
                el,
                num: el.querySelector('.item__num'),
                project: null
            }
        })
    }

    _initThemeColors() {
        const root = getComputedStyle(document.documentElement)
        this.colorMuted = root.getPropertyValue('--color-gray').trim()
        this.colorActive = root.getPropertyValue('--color-black').trim()
    }

    _cleanupListAnimations() {
        this._splitAnimation?.kill()
        this._split?.revert()
        this._split = null
        this._splitAnimation = null
    }

    // ─────────────────────────────────────────────────────────────
    // Data & Circle Slot Engine
    // ─────────────────────────────────────────────────────────────

    /** 設定基礎專案資料並分配可用槽位 */
    setProjects(projects) {
        this.projects = projects
        this.count = Math.min(projects.length, MAX_SLOTS)
        this.centerSlot = (this.count - 1) >> 1

        this.rows = this.allRows.slice(0, this.count)
        this.allRows.forEach((row, i) => {
            row.el.style.display = i < this.count ? '' : 'none'
        })
    }

    /** 依據當前中心專案進行環狀填補 (Slot Circle) */
    _fill(centerProject) {
        const total = this.projects.length

        const setSlot = (i, project) => {
            this.slotProjects[i] = project
            const row = this.rows[i]
            row.project = project
            row.num.textContent = `(${project.id})`
        }

        if (total <= MAX_SLOTS) {
            for (let i = 0; i < this.count; i++) {
                setSlot(i, this.projects[i])
            }
            return
        }

        const centerIndex = this.projects.indexOf(centerProject)
        if (centerIndex < 0) {
            return
        }

        for (let i = 0; i < this.count; i++) {
            const idx = gsap.utils.wrap(0, total, centerIndex + (i - this.centerSlot))
            setSlot(i, this.projects[idx])
        }
    }

    // ─────────────────────────────────────────────────────────────
    // Hover Handlers
    // ─────────────────────────────────────────────────────────────

    /** Hover 觸發預覽：hover 中的 num 維持黑色、其餘 num 變灰，並在左下角 Split 進出對應 Name */
    hover(project) {
        const slot = project ? this.slotProjects.indexOf(project) : -1
        if (slot === this.activeSlot) {
            return
        }

        this.rows.forEach((row, i) => {
            const dimmed = slot >= 0 && i !== slot
            gsap.to(row.el, {
                color: dimmed ? this.colorMuted : this.colorActive,
                duration: DURATION.color,
                ease: EASE.hover,
                overwrite: true
            })
        })

        if (slot >= 0) {
            this._showName(this.rows[slot].project.title)
        } else {
            this._hideName()
        }

        this.activeSlot = slot
    }

    /** 建立一個全新的 name slot 元素並掛進 DOM，回傳包好的 SplitFx */
    _createNameSlot() {
        const el = document.createElement('span')
        el.className = 'name__slot'
        this.name.appendChild(el)
        return new SplitFx(el)
    }

    /**
     * 換手：目前顯示中的 slot 立刻 splitOut，全新的 slot 立刻 splitIn
     * 每次呼叫都是各自獨立的 DOM 元素，彼此不共用、不用等對方跑完，
     * 所以可以無限快速連續觸發也不會卡頓或跳幀（比照 arock 專案：中斷不等待，直接切下一個）
     */
    _showName(title) {
        this._retireActiveName()

        const fx = this._createNameSlot()
        this._nameFxPool.add(fx)
        fx.reveal(title, { stagger: STAGGER.none, linesClass: 'name__line' })
        this._activeNameFx = fx
    }

    /** 目前顯示中的 slot splitOut，之後皆無 active slot */
    _hideName() {
        this._retireActiveName()
    }

    /**
     * 讓目前 active 的 slot 自己 splitOut，動畫完成後自行從 DOM 移除並退出 pool
     * 回傳該 tween（沒有 active slot 或文字為空時回傳 null）
     */
    _retireActiveName() {
        const fx = this._activeNameFx
        this._activeNameFx = null
        if (!fx) {
            return null
        }

        const tween = fx.conceal({ stagger: STAGGER.none, linesClass: 'name__line' })
        if (!tween) {
            this._nameFxPool.delete(fx)
            fx.el.remove()
            return null
        }

        tween.then(() => {
            this._nameFxPool.delete(fx)
            fx.el.remove()
        })

        return tween
    }

    /** 離開首頁時強制立刻 splitOut 左下角 Name，不管當下有沒有動畫正在跑 */
    _forceHideName() {
        const tween = this._retireActiveName()
        return tween ? [tween] : []
    }

    /** 重置所有列的 Hover 狀態與左下角 Name：立即中斷所有動畫並清空，不留殘影 */
    _resetHover() {
        if (this.rows.length) {
            gsap.set(
                this.rows.map((row) => {
                    return row.el
                }),
                { clearProps: 'color' }
            )
        }
        this._nameFxPool.forEach((fx) => {
            fx.revert()
        })
        this._nameFxPool.clear()
        this._activeNameFx = null
        this.name.replaceChildren()
        this.activeSlot = -1
    }

    // ─────────────────────────────────────────────────────────────
    // Motion Transitions & Updates
    // ─────────────────────────────────────────────────────────────

    /** 顯示整排 Project Num（首頁進場 / 從 Detail 返回） */
    listIn(centerProject) {
        this._fill(centerProject)
        this._cleanupListAnimations()

        const nums = this.rows.map((row) => {
            return row.num
        })
        const { split, tween } = splitReveal(nums)

        this._split = split
        this._splitAnimation = tween

        const tl = gsap.timeline()
        tl.add(tween, 0)
        return tl
    }

    /** 隱藏整排 Project Num（Home → Detail），並強制立刻 splitOut 左下角 Name */
    listOut() {
        this._cleanupListAnimations()

        const nums = this.rows.map((row) => {
            return row.num
        })
        const { split, tween } = splitConceal(nums, { stagger: STAGGER.none })

        this._split = split
        this._splitAnimation = tween

        const tl = gsap.timeline()
        tl.add(tween, 0)

        this._forceHideName().forEach((nameTween) => {
            tl.add(nameTween, 0)
        })

        return tl
    }

    /** 捲動更新：替換 Num 文字（環狀滾動） */
    update(centerProject) {
        if (this.projects.length <= MAX_SLOTS) {
            return
        }

        this._resetHover()

        if (this._split) {
            this._split.revert()
            this._split = null
        }

        this._fill(centerProject)
    }

    disconnectedCallback() {
        this._cleanupListAnimations()
        this._resetHover()
    }
}

if (!customElements.get('app-project')) {
    customElements.define('app-project', AppProject)
}

export const project = document.querySelector('app-project')
